import { makeAutoObservable } from "mobx";
import { IdLabel, getIdLabels } from "src/references/getIdNames";
import { AttrTypeName, makeDictNameById } from "src/types/AttrType";
import {
  GroupType,
  ZIdGroupLabel,
  ZIdGroupType,
  zGroupType,
  zIdGroupLabel,
} from "src/types/ZGroup";
import { loadRoleGroups, loadTypeActions } from "../../Obj2Tab/roles/rolesApi";
import { ZRolesGroup, ZTypeActions } from "../../Obj2Tab/roles/roleTypes";
import { GroupData, unsavedId } from "./node";

/**
 * Справочники, которыми формы наполняют свои поля.
 *
 * Отделены от дерева: они не зависят ни от выбранного узла, ни от загруженных
 * объектов, и живут дольше их всех. В v1 их загрузка была вшита в общий init
 * вместе с построением дерева.
 */
export class ReferencesStore {
  constructor() {
    makeAutoObservable(this);
  }

  attrTypes: IdLabel[] = [];

  groupTypes: ZIdGroupLabel[] = [];

  roleGroups: ZRolesGroup[] = [];

  typeActions: ZTypeActions[] = [];

  /**
   * Справочники, не меняющиеся в течение сессии, грузятся один раз.
   * Роли перечитываются при каждом входе на вкладку: их может изменить
   * администратор в соседней вкладке — так было и в v1.
   */
  async load(onAttrTypes: (dict: Record<number, string>) => void) {
    if (this.attrTypes.length === 0) {
      this.setAttrTypes(await getIdLabels("attrType", "attrType"));
      onAttrTypes(this.attrTypesDict);
    }
    if (this.groupTypes.length === 0) {
      this.setGroupTypes(await this.fetchGroupTypes());
    }
    this.setRoleGroups(await loadRoleGroups());
    if (this.typeActions.length === 0) {
      this.setTypeActions(await loadTypeActions());
    }
  }

  // eslint-disable-next-line class-methods-use-this
  private async fetchGroupTypes(): Promise<ZIdGroupLabel[]> {
    const raw = await getIdLabels("groupType", "groupType");
    // eslint-disable-next-line no-underscore-dangle
    const known = raw.filter(({ name }) => name in zGroupType._def.values);
    const types = zIdGroupLabel.array().parse(known);
    const byName = new Set(types.map(({ name }) => name));
    [GroupType.Mnemonic, GroupType.ByDictionary].forEach((name) => {
      if (!byName.has(name))
        throw Error(`В справочнике типов групп отсутствует ${name}`);
    });
    return types;
  }

  setAttrTypes(list: IdLabel[]) {
    this.attrTypes = list;
  }

  setGroupTypes(list: ZIdGroupLabel[]) {
    this.groupTypes = list;
  }

  setRoleGroups(list: ZRolesGroup[]) {
    this.roleGroups = list;
  }

  setTypeActions(list: ZTypeActions[]) {
    this.typeActions = list;
  }

  get attrTypesDict(): Record<number, string> {
    return makeDictNameById(this.attrTypes);
  }

  get groupTypeByName(): Record<GroupType, ZIdGroupType> {
    return this.groupTypes.reduce(
      (acc, it) => ({ ...acc, [it.name]: it }),
      {} as Record<GroupType, ZIdGroupType>,
    );
  }

  get objectTypeActions(): ZTypeActions[] {
    return this.typeActions.filter(
      ({ type }) => type === "OBJECT" || type === "ENTITY",
    );
  }

  get attrTypeActions(): ZTypeActions[] {
    // Создание и удаление атрибута задаются не здесь (LPLM-4852)
    return this.typeActions
      .filter(({ type }) => type === "ATTRIBUTE")
      .map((it) => ({
        ...it,
        actions: it.actions.filter(
          ({ name }) =>
            name !== "ATTRIBUTE_CREATE" && name !== "ATTRIBUTE_DELETE",
        ),
      }));
  }

  /** Групп со множественным справочником не бывает: как это работает, неясно */
  isDictionary1Type(typeId: number): boolean {
    return this.attrTypesDict[typeId] === AttrTypeName.dictSingle;
  }

  /** Заготовка новой группы выбранного типа. */
  newGroup(type: GroupType): GroupData {
    const found = this.groupTypeByName[type];
    if (!found) throw Error(`Не найдено описание типа группы ${type}`);
    return {
      id: unsavedId,
      name: "",
      groupType: { id: found.id, name: found.name },
    };
  }
}
